import { createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { AlertBox } from '@/common/AlertBox';
import { InfoSectionContainer } from '@/common/InfoSection';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { TabsBar } from '@/common/TabsBar';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { CheckboxLabel, WBSCheckbox } from '@/common/WBSCheckbox';
import { useAdminTabs } from '@/groups/coordinator/useAdminTabs';
import { BorderedRichTextEditor } from '@/messages/BorderedRichTextEditor';
import { PageContent } from '@/settings/settingsStyles';
import { useRichTextEditor } from '~/hooks/useRichTextEditor';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/admin/student-notice-msg',
)({
  component: StudentNoticeMsgScreen,
});

function StudentNoticeMsgScreen(): ReactElement {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const adminTabs = useAdminTabs();
  // Seeded from the stored row by the latch below, never from `true`: some
  // migrated announcements are disabled, and a `true` default would re-enable
  // one on the next save.
  const [displayAnnouncement, setDisplayAnnouncement] = useState(false);
  const [saved, setSaved] = useState(false);

  const editor = useRichTextEditor();
  const { getEditorContent, getEditorHtml, setEditorHtml } = editor;

  // qraft evaluates `parameters` before reading `enabled`, so 0 (never a real
  // id) stands in until the slug resolves.
  const { data: group, isError: groupFailed } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = group?.id ?? 0;

  // The slug read isn't the refusal signal here: on a public group a
  // non-member's slug read succeeds, and the 403 arrives on this query instead.
  const { data: announcement, isError: announcementFailed } =
    api.getGroupAnnouncement.useQuery(
      { path: { group_id: groupId } },
      { enabled: groupId > 0 },
    );

  const {
    mutateAsync: saveAnnouncement,
    isPending: saving,
    error: saveFailed,
  } = api.setGroupAnnouncement.useMutation();

  // Latched on the group id rather than a boolean: the seed must not re-run on
  // the post-save refetch, which would discard what the helper has typed since,
  // but must run again if the component is reused for another group.
  const seededGroupId = useRef<number | null>(null);
  useEffect(() => {
    if (!announcement) return;
    if (seededGroupId.current === announcement.group_id) return;
    seededGroupId.current = announcement.group_id;
    setEditorHtml(announcement.content);
    setDisplayAnnouncement(announcement.is_enabled);
  }, [announcement, setEditorHtml]);

  const handleSave = async (): Promise<void> => {
    // The button is disabled while saving, but Shift+Enter submits around it.
    // The write has no idempotency key, so a second call would store the row
    // twice and notify the students twice.
    if (saving) return;
    setSaved(false);
    try {
      await saveAnnouncement({
        path: { group_id: groupId },
        // HTML, since that's the stored format. The plain-text read still
        // decides emptiness, because the editor seeds itself with
        // `<div><br></div>` on focus, and a Shift+Enter save would otherwise
        // store a blank line instead of an empty string.
        body: {
          content: getEditorContent() ? getEditorHtml() : '',
          is_enabled: displayAnnouncement,
        },
      });
      setSaved(true);
      await api.getGroupAnnouncement.invalidateQueries();
    } catch {
      // Rendered from `saveFailed` below.
    }
  };

  // A refused read shows an error, not the form. The layout guard admits a
  // platform admin, but this endpoint admits only the group's leaders, so
  // without this arm they'd get an authoring form whose save 403s. Legacy
  // answers "Page not found".
  //
  // Each arm also requires that no data is held: a failed refetch after a save
  // sets the error on a query whose record is still on screen, and the form
  // mustn't blank out content being edited.
  const refused =
    (groupFailed && group === undefined) ||
    (announcementFailed && announcement === undefined);
  const pending =
    !refused && (group === undefined || announcement === undefined);

  return (
    <PageContent>
      <TabsBar tabs={adminTabs} base="/groups/$groupName/admin" />
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('lessons.student-announcement')}
          placement="study_helper_group_settings"
        />
      </GroupHeaderWrapper>
      {refused ? (
        <AlertBox variant="error">{t('wbs.page-not-found')}</AlertBox>
      ) : pending ? (
        // Never a blank form: the latched effect is about to write the stored
        // announcement into an editor that has already painted empty.
        <LoadingSpinner centered />
      ) : (
        <InfoSectionContainer>
          {saved && (
            <AlertBox variant="success">
              {t('lessons.announcement-updated-successfully')}
            </AlertBox>
          )}
          {saveFailed && (
            // A failed WRITE keeps its screen, unlike a refused read: the
            // editor still holds content that exists nowhere else.
            <AlertBox variant="error">
              {t('wbs.something-went-wrong-try')}
            </AlertBox>
          )}
          <CheckboxRow>
            <WBSCheckbox
              id="display-announcement"
              checked={displayAnnouncement}
              onChange={(e) => setDisplayAnnouncement(e.target.checked)}
            />
            <CheckboxLabel htmlFor="display-announcement">
              {t('wbs.display-announcement')}
            </CheckboxLabel>
          </CheckboxRow>
          {/* Not disabled when the announcement is switched off: as in
             legacy, that's how a helper edits a draft before enabling it. */}
          <BorderedRichTextEditor
            editor={editor}
            id="announcement-editor"
            visibleTools={['bold', 'italic', 'underline', 'blockquote']}
            maxHeight="400px"
            placeholder={t('lessons.write-an-announcement')}
            onSubmit={() => {
              void handleSave();
            }}
          />
          <SaveRow>
            <WBSButton
              wide
              type="button"
              isLoading={saving}
              disabled={saving}
              onClick={() => {
                void handleSave();
              }}
            >
              {t('actions.save-changes')}
            </WBSButton>
          </SaveRow>
        </InfoSectionContainer>
      )}
    </PageContent>
  );
}

const CheckboxRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;
